import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { STRINGS } from "@/content/strings";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setOverrides(r.data.strings || {})).catch(() => {});
  }, []);

  const get = useCallback(
    (key, fallback) => overrides[key] ?? STRINGS[key] ?? (fallback || ""),
    [overrides]
  );

  const updateString = useCallback(async (key, value, screen, previous) => {
    setOverrides((o) => ({ ...o, [key]: value }));
    try {
      await api.put("/content", { key, value, screen, previous });
      toast.success("Wording updated everywhere");
    } catch (e) {
      // roll back the optimistic change on failure
      setOverrides((o) => {
        const next = { ...o };
        if (previous === undefined) delete next[key]; else next[key] = previous;
        return next;
      });
      toast.error(e.response?.data?.detail || "Could not save the change");
    }
  }, []);

  const resetString = useCallback(async (key) => {
    try {
      await api.delete(`/content/${encodeURIComponent(key)}`);
      setOverrides((o) => { const next = { ...o }; delete next[key]; return next; });
      toast.success("Reset to default");
    } catch (e) {
      toast.error("Could not reset");
    }
  }, []);

  return (
    <ContentContext.Provider value={{ get, overrides, editMode, setEditMode, updateString, resetString }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext) || {
  get: (k, f) => STRINGS[k] ?? f ?? "", overrides: {}, editMode: false, setEditMode: () => {}, updateString: async () => {}, resetString: async () => {},
};
